import { useSnackbarContext } from "@ameelio/ui";
import { ApolloProvider } from "@apollo/client/react";
import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "./App.css";
import getAuthenticatedClient from "./client";
import {
  buildApolloErrorMessages,
  reportApolloError,
} from "./lib/handleApolloError";

type Props = {
  children: React.ReactNode;
};

export default function AuthenticatedClientProvider({ children }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { alert } = useSnackbarContext();

  // useNavigate returns a new function on every pathname change, so logout
  // reads it through a ref. Depending on navigate directly rebuilt the client
  // on every navigation, which made mounted cache-and-network queries refetch.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const logout = useCallback(
    (tokenError?: boolean) =>
      navigateRef.current("/logout", { state: { tokenError } }),
    []
  );

  // The Apollo client is built once (keyed on the stable logout/notify
  // identities). notify stays stable via a ref so a new alert/t never
  // forces the client to be rebuilt, while still calling the latest ones.
  const notifyRef = useRef<(error: unknown) => void>(() => undefined);
  notifyRef.current = (error: unknown) =>
    reportApolloError(error, {
      alert,
      errorMessages: buildApolloErrorMessages(t),
    });
  const notify = useCallback((error: unknown) => notifyRef.current(error), []);

  const apolloClient = useMemo(
    () => getAuthenticatedClient({ logout, notify }),
    [logout, notify]
  );

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
}
